'use client';

import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { DisputeStatusBadge } from '@/components/disputes/dispute-status-badge';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useDisputeDetails } from '@/lib/api/disputes';
import { BackButton } from '@/components/ui/BackButton';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import DisputeDetailsSkeleton from './components/skeleton';
import { DisputeOverviewCard } from './components/dispute-overview-card';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(DisputeDetailsPage, PERMISSIONS.PAYMENTS_VIEW);

function DisputeDetailsPage() {
  const { t } = useTranslation('disputes');
  const { id } = useParams();
  const { data: dispute, isLoading, error } = useDisputeDetails(id!);
  const { orgRoutes } = useOrgNavigate();

  if (isLoading) {
    return <DisputeDetailsSkeleton />;
  }

  if (error) {
    const parsed = parseTRPCError(error);
    if (parsed.code === 'NOT_FOUND') {
      return <Navigate to={orgRoutes.DISPUTES()} replace state={{ disputeNotFound: id }} />;
    }
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <ErrorDisplay error={parsed} />
      </div>
    );
  }

  if (!dispute) {
    return <Navigate to={orgRoutes.DISPUTES()} replace state={{ disputeNotFound: id }} />;
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <BackButton to={orgRoutes.DISPUTES()} />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{t('disputeDetails')}</h1>
                <DisputeStatusBadge status={dispute.status} />
              </div>
            </div>
          </div>
          <CopyableResourceBadge name={t('table.columns.disputeId')} value={dispute.id} variant="outline" />
        </div>

        {/* Main Content */}
        <div className="mt-6">
          <DisputeOverviewCard dispute={dispute} />
        </div>
      </div>
    </div>
  );
}
